import {
  BF_DREAMS_SAVE_FAILED,
  BF_PLEDGE_CLOSED_MESSAGE,
  BF_PLEDGE_YEAR,
} from '@/constants/MyGiving/building-fund-pledge';
import * as pledgeApi from '@/utils/building-fund-pledge-api';
import { isBfPledgeHarnessActive } from '@/utils/bf-pledge-harness';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { isBfPledgeOpen, isYouthByAge, toPledgeBody } from '@/utils/building-fund-pledge';
import * as dreamsStorage from '@/utils/bf-dreams-prayers';
import { isGivingLoginUnverified } from '@/utils/giving-login';

// The BF2026 digital pledge: the member's pledge and eligibility for the campaign
// year, and what they last submitted. There are no drafts (decided 2026-10-01).
// Kept out of myGiving.js, which is already large. The contract with the pages is
// §4 of docs/plans/2026-09-29-building-fund-pledge-implementation.md.

const initialState = () => ({
  pledge: null,
  eligibility: null,
  loading: false,
  error: null,
  // The body the member sent. POST /pledges/confirm answers with an empty 201, so
  // the done screen renders from this rather than from a response. A one-time
  // pledge goes on to Give now, not to the done screen, so it leaves this null: set,
  // it would keep /done reachable for the rest of the session.
  submitted: null,
  // The body the confirm page (C1, C2) shows and sends: a five-month or sow pledge
  // the member has filled in but not yet submitted. Cleared once it is sent or the
  // member goes back to the form; the confirm route needs it.
  pending: null,
  // "My Dreams & Prayers" (src/utils/bf-dreams-prayers.js). `null` until loaded
  // for the signed-in member; D and V both show this one copy.
  dreams: null,
  // GET /bf/fulfilment for one year (My BF Fulfilment, 2026 on). `null` until loaded.
  fulfilment: null,
});

const state = initialState;

const getters = {
  hasPledge: state => Boolean(state.pledge),
};

const mutations = {
  setPledge(state, pledge) {
    state.pledge = pledge ?? null;
  },
  setEligibility(state, eligibility) {
    state.eligibility = eligibility ?? null;
  },
  setLoading(state, loading) {
    state.loading = loading;
  },
  setError(state, error) {
    state.error = error;
  },
  setSubmitted(state, body) {
    state.submitted = body;
  },
  setPending(state, body) {
    state.pending = body;
  },
  setDreams(state, text) {
    state.dreams = text;
  },
  setFulfilment(state, fulfilment) {
    state.fulfilment = fulfilment ?? null;
  },
  reset(state) {
    Object.assign(state, initialState());
  },
};

// Runs one API call with the loading flag, the error state and the toast handled
// the same way for every action. Returns the payload, or `undefined` on failure.
// `quiet` skips the toast, for loads the member did not ask for.
async function request({ commit, dispatch, rootState }, run, fallback, { quiet = false } = {}) {
  commit('setLoading', true);
  commit('setError', null);
  try {
    return await run(rootState.user.token);
  } catch (error) {
    // A login that has not confirmed the personal id: the pledge is keyed by it. The
    // giving-login interceptor takes the member to the check, so no toast here.
    if (isGivingLoginUnverified(error)) {
      commit('myGiving/markLoginUnverified', null, { root: true });
      return undefined;
    }
    const message = pledgeApi.pledgeErrorMessage(error, fallback);
    commit('setError', message);
    if (!quiet) dispatch('toast/error', message, { root: true });
    return undefined;
  } finally {
    commit('setLoading', false);
  }
}

// The request body for what the member typed. A member who is a youth by their
// profile's age goes in as one even if the box is unticked, as on the web pledge form.
function bodyFor({ rootState }, form) {
  return toPledgeBody(form, { youthByAge: isYouthByAge(rootState.profile) });
}

// Re-reads BF_PLEDGE_OPEN_FLAG from the server: flags are only loaded at launch,
// and the window can close while the app is open. A failed refresh keeps the last
// value. Skipped while the /bf-pledge-states harness is on, whose window toggle
// seeds the flag locally and would be overwritten.
async function refreshPledgeWindow({ rootState }) {
  if (isBfPledgeHarnessActive()) return;
  await useFeatureFlags().loadUserFlags(rootState.user.token);
}

// POSTs the body. On success the pledge is refreshed, as the landing banner and V
// read it; quietly, as the pledge is in and a failed refresh is not worth a toast.
async function sendPledge(context, body) {
  // The window closed (BF_PLEDGE_OPEN_FLAG off) while the member was on Confirm: send
  // nothing. Flags are otherwise only loaded at launch, so the flag is re-read first;
  // an app left open across the cutoff would still see it on. The route guard keeps
  // Confirm closed to anyone arriving later.
  await refreshPledgeWindow(context);
  if (!isBfPledgeOpen()) {
    context.commit('setError', BF_PLEDGE_CLOSED_MESSAGE);
    context.dispatch('toast/error', BF_PLEDGE_CLOSED_MESSAGE, { root: true });
    return false;
  }
  let accepted = false;
  await request(
    context,
    async token => {
      await pledgeApi.submitPledge(token, body);
      accepted = true;
    },
    'Failed to submit your pledge. Please try again.',
  );
  if (!accepted) return false;
  try {
    context.commit(
      'setPledge',
      await pledgeApi.fetchPledge(context.rootState.user.token, body.year),
    );
  } catch {
    /* V refetches on entry */
  }
  return true;
}

const actions = {
  // The form calls this on entry, so its Continue reflects the window as it is now.
  refreshPledgeWindow,

  // `quiet` is for the MyGiving landing, which loads this behind the PIN overlay
  // only to label its banner: a failure there is not worth a toast over the PIN pad.
  async fetchPledge(context, { year = BF_PLEDGE_YEAR, quiet = false } = {}) {
    const pledge = await request(
      context,
      token => pledgeApi.fetchPledge(token, year),
      'Failed to retrieve your pledge. Please try again.',
      { quiet },
    );
    if (pledge !== undefined) context.commit('setPledge', pledge);
    return pledge;
  },

  // The previous year's numbers are cleared first, so switching years never shows
  // them under the new year while the call is in flight.
  async fetchFulfilment(context, year) {
    context.commit('setFulfilment', null);
    const fulfilment = await request(
      context,
      token => pledgeApi.fetchFulfilment(token, year),
      'Failed to retrieve your Building Fund fulfilment. Please try again.',
    );
    if (fulfilment !== undefined) context.commit('setFulfilment', fulfilment);
    return fulfilment;
  },

  async fetchEligibility(context, year = BF_PLEDGE_YEAR) {
    const eligibility = await request(
      context,
      token => pledgeApi.fetchEligibility(token, year),
      'Failed to check whether you can pledge. Please try again.',
    );
    if (eligibility !== undefined) context.commit('setEligibility', eligibility);
    return eligibility;
  },

  /** Keeps the pledge for the confirm page to show and send. */
  reviewPledge(context, form) {
    context.commit('setPending', bodyFor(context, form));
  },

  /**
   * Sends the pledge the confirm page shows. Resolves true once giving-api has
   * accepted it; the done page then renders it from `submitted`.
   */
  async confirmPledge(context) {
    const body = context.state.pending;
    if (!body) return false;
    const accepted = await sendPledge(context, body);
    if (!accepted) return false;
    context.commit('setSubmitted', body);
    context.commit('setPending', null);
    return true;
  },

  // On the device, not giving-api, so neither of these uses request(). Read once
  // per member: D and V each ask, and saveDreams keeps the copy current.
  // Sign-out's reset sets it back to null, so a loaded copy is this member's.
  async loadDreams({ commit, state, rootState }) {
    if (state.dreams !== null) return;
    try {
      const text = await dreamsStorage.loadDreams(rootState.profile?.id);
      if (text !== null) commit('setDreams', text);
    } catch {
      // Nothing to show is the same as nothing saved; the member can still type.
    }
  },

  /** Saves the text on this device. Resolves true once saved. */
  async saveDreams({ commit, dispatch, rootState }, text) {
    const value = text.trim();
    try {
      await dreamsStorage.saveDreams(rootState.profile?.id, value);
    } catch {
      dispatch('toast/error', BF_DREAMS_SAVE_FAILED, { root: true });
      return false;
    }
    commit('setDreams', value);
    return true;
  },

  reset({ commit }) {
    commit('reset');
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
